// ============================================================================
// Tienda online — pure rules shared by the storefront and the account's panel
// (backend/online_store.sql is the authority: it re-prices every order).
// ============================================================================

export const STORE_FEATURE = 'online_store';
export const STORE_PATH = '/tienda';

export const PAYMENT_METHODS = ['cash', 'card_on_delivery', 'bizum', 'transfer', 'online_card'];
// The order's journey; `cancelled` can happen from any step before delivered.
export const ORDER_STATUSES = ['new', 'accepted', 'preparing', 'ready', 'shipped', 'delivered', 'cancelled'];
export const OPEN_STATUSES = ['new', 'accepted', 'preparing', 'ready', 'shipped'];

const str = (v, max) => String(v ?? '').trim().slice(0, max);
const num = (v) => {
  const n = typeof v === 'number' ? v : parseFloat(String(v ?? '').replace(',', '.'));
  return Number.isFinite(n) && n > 0 ? Math.round(n * 100) / 100 : 0;
};
// A percentage typed by the account: "10", "12,5", "-5" → within -90 … 500.
const percent = (v) => {
  const n = typeof v === 'number' ? v : parseFloat(String(v ?? '').replace(',', '.'));
  return Number.isFinite(n) ? Math.min(500, Math.max(-90, Math.round(n * 100) / 100)) : 0;
};
const colour = (v, fallback) => (/^#[0-9a-fA-F]{6}$/.test(String(v || '')) ? v : fallback);

export const DEFAULT_SETTINGS = {
  name: '',
  tagline: '',
  logo_url: '',
  banner_url: '',
  primary: '#2e7d32',
  phone: '',
  email: '',
  whatsapp: '',
  address: '',
  hours: '',
  website: '',           // the business's other website, linked in the footer
  notice: '',            // a line across the top ("Reparto gratis desde 50 €")
  orders_paused: false,
  price_percent: 0,      // «incremento web»: every price ± this % (an article's own web price wins)
  delivery: { enabled: true, fee: 0, free_from: 0, min_order: 0, postcodes: [], note: '' },
  pickup: { enabled: true, note: '' },
  payments: { cash: true, card_on_delivery: false, bizum: false, transfer: false, online_card: false },
  transfer_iban: '',
  bizum_phone: '',
  catalog: { only_with_photo: false, hide_out_of_stock: false, sell_without_stock: false },
  legal: '',
};

/** Whatever is stored → a complete, safe settings object. */
export function normaliseSettings(raw) {
  const s = raw && typeof raw === 'object' ? raw : {};
  const d = s.delivery && typeof s.delivery === 'object' ? s.delivery : {};
  const p = s.pickup && typeof s.pickup === 'object' ? s.pickup : {};
  const pay = s.payments && typeof s.payments === 'object' ? s.payments : {};
  const c = s.catalog && typeof s.catalog === 'object' ? s.catalog : {};
  const D = DEFAULT_SETTINGS;
  return {
    name: str(s.name, 80),
    tagline: str(s.tagline, 160),
    logo_url: str(s.logo_url, 500),
    banner_url: str(s.banner_url, 500),
    primary: colour(s.primary, D.primary),
    phone: str(s.phone, 40),
    email: str(s.email, 160),
    whatsapp: str(s.whatsapp, 40),
    address: str(s.address, 300),
    hours: str(s.hours, 300),
    website: str(s.website, 300),
    notice: str(s.notice, 200),
    orders_paused: !!s.orders_paused,
    price_percent: percent(s.price_percent),
    delivery: {
      enabled: d.enabled === undefined ? D.delivery.enabled : !!d.enabled,
      fee: num(d.fee),
      free_from: num(d.free_from),
      min_order: num(d.min_order),
      postcodes: parsePostcodes(d.postcodes),
      note: str(d.note, 300),
    },
    pickup: { enabled: p.enabled === undefined ? D.pickup.enabled : !!p.enabled, note: str(p.note, 300) },
    payments: Object.fromEntries(PAYMENT_METHODS.map((m) => [m, pay[m] === undefined ? D.payments[m] : !!pay[m]])),
    transfer_iban: str(s.transfer_iban, 40),
    bizum_phone: str(s.bizum_phone, 40),
    catalog: {
      only_with_photo: !!c.only_with_photo,
      hide_out_of_stock: !!c.hide_out_of_stock,
      sell_without_stock: !!c.sell_without_stock,
    },
    legal: str(s.legal, 6000),
  };
}

/** "25001, 25002 25003" or an array → unique clean postcodes. */
export function parsePostcodes(v) {
  const list = Array.isArray(v) ? v : String(v || '').split(/[\s,;]+/);
  return Array.from(new Set(list.map((z) => String(z || '').replace(/\s/g, '').toUpperCase()).filter((z) => /^[0-9A-Z-]{3,10}$/.test(z)))).slice(0, 500);
}

/** A business name → a web address part: "Súper Ñam & Co." → "super-nam-co". */
export function slugify(name) {
  return String(name || '')
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
    .slice(0, 60).replace(/-+$/g, '');
}

export const isValidSlug = (s) => /^[a-z0-9]+(-[a-z0-9]+)*$/.test(s || '') && s.length >= 3 && s.length <= 60;

/** "https://www.Mi-Tienda.es/x" → "mi-tienda.es"; '' when it is not a domain. */
export function cleanDomain(v) {
  const host = String(v || '').trim().toLowerCase().replace(/^[a-z]+:\/\//, '').replace(/[/?#].*$/, '').replace(/:\d+$/, '').replace(/^www\./, '');
  return /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/.test(host) ? host : '';
}

// Hosts that serve zBilling itself; any other host may be a shop's own domain.
const PLATFORM_HOSTS = ['app.zcom.es', 'localhost', '127.0.0.1', '10.0.2.2'];
export function isPlatformHost(hostname, extra = process.env.REACT_APP_PLATFORM_HOSTS) {
  const h = String(hostname || '').toLowerCase();
  if (!h || PLATFORM_HOSTS.includes(h) || /^[0-9.]+$/.test(h) || h.endsWith('.onrender.com') || h.endsWith('.local')) return true;
  return String(extra || '').split(',').map((x) => x.trim().toLowerCase()).filter(Boolean).includes(h);
}

/** The shop's public address: its own domain when it has one. */
export function storeUrl(store, origin = typeof window !== 'undefined' ? window.location.origin : '') {
  if (!store) return '';
  return store.custom_domain ? `https://${store.custom_domain}` : `${origin}/${store.slug}/store`;
}

/**
 * What the shop charges for an article, IVA included — the same rule as
 * store_price() in backend/online_store.sql: its own web price, else the
 * normal price moved by the shop's percentage (the drinks tax stays on top).
 */
export function webPrice(product, pricePercent) {
  if (Number(product.web_price) > 0) return Math.round(Number(product.web_price) * 100) / 100;
  const gross = (Number(product.unit_price) || 0) * (1 + (Number(product.tax_rate) || 0) / 100);
  const tax = (Number(product.ibi) || 0) * (Number(product.volume_l) || 0);
  return Math.round((gross * (1 + percent(pricePercent) / 100) + tax + Number.EPSILON) * 100) / 100;
}

// Photos uploaded from Productos since this date also have a small copy
// (…_t.jpg) for the shelves, so a page of 24 products downloads ~10x less.
export const THUMBS_SINCE = 1791000000000;
export const THUMB_SIZE = 320;

/** The small copy of a product photo, or null when it has none. */
export function thumbUrl(url) {
  const m = /^(.*\/product_(\d{13}))\.jpg$/.exec(String(url || ''));
  return m && Number(m[2]) >= THUMBS_SINCE ? `${m[1]}_t.jpg` : null;
}

// --- money -------------------------------------------------------------------

const cents = (n) => Math.round((Number(n) || 0) * 100);

export function formatMoney(n, lang = 'es') {
  return new Intl.NumberFormat(lang === 'en' ? 'en-GB' : `${lang}-ES`, { style: 'currency', currency: 'EUR' }).format(Number(n) || 0);
}

/** Quantity step and floor for one article (kilos for articles sold by weight). */
export const qtyStep = (product) => (product?.by_weight ? 0.25 : 1);

export function clampQty(product, qty) {
  const step = qtyStep(product);
  const n = Number(qty) || 0;
  if (n <= 0) return 0;
  const snapped = product?.by_weight ? Math.round(n * 1000) / 1000 : Math.floor(n);
  return Math.min(9999, Math.max(step, snapped));
}

/**
 * What the cart shows before ordering. Same arithmetic as store_place_order():
 * each line rounded to the cent, delivery free from `free_from`.
 * `lines` = [{ price, qty }] with IVA-included prices.
 */
export function cartTotals(lines, settings, fulfilment) {
  const subtotal = (lines || []).reduce((sum, l) => sum + Math.round((Number(l.price) || 0) * (Number(l.qty) || 0) * 100), 0);
  const d = settings?.delivery || {};
  let fee = 0;
  if (fulfilment === 'delivery') {
    fee = cents(d.fee);
    if (cents(d.free_from) > 0 && subtotal >= cents(d.free_from)) fee = 0;
  }
  const missing = fulfilment === 'delivery' ? Math.max(0, cents(d.min_order) - subtotal) : 0;
  const toFree = fulfilment === 'delivery' && cents(d.free_from) > 0 && cents(d.fee) > 0 ? Math.max(0, cents(d.free_from) - subtotal) : 0;
  return {
    subtotal: subtotal / 100,
    delivery_fee: fee / 100,
    total: (subtotal + fee) / 100,
    below_minimum: missing / 100,     // how much is left to reach the minimum order
    to_free_delivery: toFree / 100,   // how much is left for free delivery
  };
}

/** IVA contained in an order's lines, by rate: [{rate, base, vat}]. */
export function vatBreakdown(items) {
  const byRate = new Map();
  (items || []).forEach((it) => {
    const rate = Number(it.tax_rate) || 0;
    byRate.set(rate, (byRate.get(rate) || 0) + cents(it.total));
  });
  return Array.from(byRate.entries()).sort((a, b) => a[0] - b[0]).map(([rate, gross]) => {
    const base = Math.round(gross / (1 + rate / 100));
    return { rate, base: base / 100, vat: (gross - base) / 100 };
  });
}

export const servesPostcode = (settings, postcode) => {
  const list = settings?.delivery?.postcodes || [];
  return list.length === 0 || list.includes(String(postcode || '').replace(/\s/g, '').toUpperCase());
};

// --- reports -----------------------------------------------------------------

const dayKey = (iso) => {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/**
 * Sales report over a list of orders. Cancelled orders count as cancelled and
 * nowhere else. Returns totals, a per-day series, top products, and splits by
 * payment method and by delivery / pickup.
 */
export function salesReport(orders) {
  const live = (orders || []).filter((o) => o.status !== 'cancelled');
  const sum = (list, f) => list.reduce((s, o) => s + cents(f(o)), 0) / 100;
  const days = new Map();
  const products = new Map();
  const payments = new Map();
  const customers = new Set();
  live.forEach((o) => {
    const k = dayKey(o.created_at);
    const day = days.get(k) || { day: k, orders: 0, total: 0 };
    day.orders += 1;
    day.total = (cents(day.total) + cents(o.total)) / 100;
    days.set(k, day);
    const pm = payments.get(o.payment_method) || { method: o.payment_method, orders: 0, total: 0 };
    pm.orders += 1;
    pm.total = (cents(pm.total) + cents(o.total)) / 100;
    payments.set(o.payment_method, pm);
    customers.add(o.customer_id || o.customer_email || o.customer_phone || o.id);
    (o.items || []).forEach((it) => {
      const key = it.product_id || it.name;
      const p = products.get(key) || { product_id: it.product_id, name: it.name, qty: 0, total: 0 };
      p.qty = Math.round((p.qty + (Number(it.qty) || 0)) * 1000) / 1000;
      p.total = (cents(p.total) + cents(it.total)) / 100;
      products.set(key, p);
    });
  });
  const total = sum(live, (o) => o.total);
  return {
    orders: live.length,
    cancelled: (orders || []).length - live.length,
    total,
    average: live.length ? Math.round((total / live.length) * 100) / 100 : 0,
    paid: sum(live.filter((o) => o.payment_status === 'paid'), (o) => o.total),
    pending: sum(live.filter((o) => o.payment_status === 'pending'), (o) => o.total),
    delivery_fees: sum(live, (o) => o.delivery_fee),
    customers: customers.size,
    delivery: live.filter((o) => o.fulfilment === 'delivery').length,
    pickup: live.filter((o) => o.fulfilment === 'pickup').length,
    days: Array.from(days.values()).sort((a, b) => a.day.localeCompare(b.day)),
    top_products: Array.from(products.values()).sort((a, b) => b.total - a.total).slice(0, 20),
    payments: Array.from(payments.values()).sort((a, b) => b.total - a.total),
  };
}

/** The steps an order may move to from `status` (the panel's buttons). */
export function nextStatuses(order) {
  const flow = order.fulfilment === 'pickup'
    ? ['new', 'accepted', 'preparing', 'ready', 'delivered']
    : ['new', 'accepted', 'preparing', 'shipped', 'delivered'];
  const i = flow.indexOf(order.status);
  if (order.status === 'cancelled' || order.status === 'delivered') return [];
  const next = i >= 0 && i < flow.length - 1 ? [flow[i + 1]] : [];
  return [...next, 'cancelled'];
}
